import React from 'react';
import { WorkExperience } from '../../../../../utils/types/JobOpenings';

interface IExpereienceTab {
  experience?: WorkExperience[];
}

interface GroupedExperience {
  company: string;
  roles: WorkExperience[];
  totalDuration: string;
  startDate: number;
  endDate: number | null;
}

export const ExperienceTab = (props: IExpereienceTab) => {
  const { experience } = props;

  const formatDate = (timestamp: number): string | null => {
    if (!timestamp) return null;
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
    });
  };

  const getDifferenceInYears = (
    start?: number,
    end?: number,
  ): string => {
    if (!start) return '';

    const startDate = new Date(start * 1000);
    const endDate = end ? new Date(end * 1000) : new Date();

    const diffTime = endDate.getTime() - startDate.getTime();
    const diffYears = Math.floor(diffTime / (1000 * 60 * 60 * 24 * 365.25));
    const diffMonths = Math.floor((diffTime % (1000 * 60 * 60 * 24 * 365.25)) / (1000 * 60 * 60 * 24 * 30.44));

    if (diffYears === 0 && diffMonths === 0) return 'Less than a month';
    if (diffYears === 0) return `${diffMonths} ${diffMonths === 1 ? 'mo' : 'mos'}`;
    if (diffMonths === 0) return `${diffYears} ${diffYears === 1 ? 'yr' : 'yrs'}`;
    return `${diffYears} ${diffYears === 1 ? 'yr' : 'yrs'} ${diffMonths} ${diffMonths === 1 ? 'mo' : 'mos'}`;
  };

  const calculateTotalExperience = (): string => {
    if (!experience || experience.length === 0) return '';

    let totalMonths = 0;
    experience.forEach(exp => {
      if (exp.startDate) {
        const start = new Date(exp.startDate * 1000);
        const end = exp.endDate ? new Date(exp.endDate * 1000) : new Date();
        const diff = end.getTime() - start.getTime();
        totalMonths += Math.floor(diff / (1000 * 60 * 60 * 24 * 30.44));
      }
    });

    const years = Math.floor(totalMonths / 12);
    const months = totalMonths % 12;

    if (years === 0) return `${months} ${months === 1 ? 'mo' : 'mos'}`;
    if (months === 0) return `${years} ${years === 1 ? 'yr' : 'yrs'}`;
    return `${years} ${years === 1 ? 'yr' : 'yrs'} ${months} ${months === 1 ? 'mo' : 'mos'}`;
  };

  // Group experiences by company
  const groupExperiencesByCompany = (): GroupedExperience[] => {
    if (!experience || experience.length === 0) return [];

    const grouped = experience.reduce((acc, exp) => {
      const company = exp.company || 'Unknown Company';

      if (!acc[company]) {
        acc[company] = [];
      }
      acc[company].push(exp);
      return acc;
    }, {} as Record<string, WorkExperience[]>);

    // Convert to array and calculate total duration for each company
    return Object.entries(grouped).map(([company, roles]) => {
      // Sort roles by start date (most recent first)
      const sortedRoles = roles.sort((a, b) => (b.startDate || 0) - (a.startDate || 0));

      // Get earliest start date and latest end date
      const startDate = Math.min(...sortedRoles.map(r => r.startDate || 0));
      const endDate = Math.max(...sortedRoles.map(r => r.endDate || Date.now() / 1000));
      const hasCurrentRole = sortedRoles.some(r => !r.endDate);

      return {
        company,
        roles: sortedRoles,
        totalDuration: getDifferenceInYears(startDate, hasCurrentRole ? undefined : endDate),
        startDate,
        endDate: hasCurrentRole ? null : endDate,
      };
    }).sort((a, b) => (b.startDate || 0) - (a.startDate || 0));
  };

  const groupedExperiences = groupExperiencesByCompany();

  return (
    <div className='animate-fadeIn py-2'>
      <div className='mb-4 border-b border-gray-200'>
        <h3 className='text-sm font-bold text-gray-900 mb-1'>Experience</h3>
        { experience && experience.length > 0 && (
          <p className='text-xs text-gray-600'>
            Total: { calculateTotalExperience() }
          </p>
        ) }
      </div>

      { groupedExperiences.length > 0 ? (
        <div className='space-y-6'>
          { groupedExperiences.map((group, groupIndex) => (
            <div key={groupIndex} className='relative'>
              { /* Company-level timeline dot and line */ }
              <div className='absolute left-0 top-1'>
                <div className='w-2 h-2 bg-blue-600 rounded-full'></div>
                { groupIndex !== groupedExperiences.length - 1 && (
                  <div
                      className='absolute left-1/2 top-2 -translate-x-1/2 w-0.5 bg-gray-300'
                      style={{ height: 'calc(100% + 24px)' }}
                  ></div>
                ) }
              </div>

              { /* Company content */ }
              <div className='pl-8'>
                { /* Company header */ }
                <div className='mb-3'>
                  <div className='flex items-start justify-between gap-4 mb-1'>
                    <h4 className='text-[13px] font-bold text-gray-900 leading-tight'>
                      { group.company }
                    </h4>
                    <span className='text-xs text-gray-600 whitespace-nowrap flex-shrink-0 font-medium'>
                      { group.totalDuration }
                    </span>
                  </div>
                  <p className='text-xs text-gray-500'>
                    { formatDate(Number(group.startDate)) } - { group.endDate ? formatDate(Number(group.endDate)) : 'Present' }
                  </p>
                </div>

                { /* Roles within the company */ }
                <div className='space-y-4 border-l-2 border-gray-200 ml-1'>
                  { group.roles.map((role, roleIndex) => (
                    <div key={roleIndex} className='relative pl-6'>
                      { /* Role timeline dot */ }
                      <div className='absolute left-0 top-1 -translate-x-1/2'>
                        <div className='w-1.5 h-1.5 bg-gray-400 rounded-full'></div>
                      </div>

                      { /* Role content */ }
                      <div>
                        <div className='flex items-start justify-between gap-4 mb-1'>
                          <h5 className='text-[13px] font-semibold text-gray-800 leading-tight'>
                            { role.jobTitle }
                          </h5>
                          <span className='text-xs text-gray-500 whitespace-nowrap flex-shrink-0'>
                            { getDifferenceInYears(role.startDate, role.endDate) }
                          </span>
                        </div>

                        <p className='text-xs text-gray-500 mb-2'>
                          { formatDate(Number(role.startDate)) } - { role.endDate ? formatDate(Number(role.endDate)) : 'Present' }
                        </p>

                        { role.description && (
                          <p className='text-xs text-gray-600 leading-relaxed mt-2'>
                            { role.description }
                          </p>
                        ) }
                      </div>
                    </div>
                  )) }
                </div>
              </div>
            </div>
          )) }
        </div>
      ) : (
        <div className='text-center py-8'>
          <svg
              className='w-12 h-12 text-gray-300 mx-auto mb-3'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
          >
            <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z'
            />
          </svg>
          <p className='text-sm text-gray-500'>No work experience available</p>
        </div>
      ) }
    </div>
  );
};
